<template>
  <div class="border rounded-lg has-background-white">
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive, getMarkAttrs }">
      <div class="border-b pa-xs">
        <div class="buttons are-small is-marginless">
          <b-button :class="{ 'is-active': isActive.bold() }" title="Bold" @click.prevent="commands.bold">
            <b-icon icon="format-bold" size="is-small" />
          </b-button>
          <b-button :class="{ 'is-active': isActive.italic() }" title="Italic" @click.prevent="commands.italic">
            <b-icon icon="format-italic" size="is-small" />
          </b-button>
          <b-button :class="{ 'is-active': isActive.underline() }" title="Underline" @click.prevent="commands.underline">
            <b-icon icon="format-underline" size="is-small" />
          </b-button>
          <b-button :class="{ 'is-active': isActive.heading({ level: 3 }) }" title="Heading" @click.prevent="commands.heading({ level: 3 })">
            <b-icon icon="format-header-3" size="is-small" />
          </b-button>
          <b-button :class="{ 'is-active': isActive.heading({ level: 4 }) }" title="Subheading" @click.prevent="commands.heading({ level: 4 })">
            <b-icon icon="format-header-4" size="is-small" />
          </b-button>
          <b-button :class="{ 'is-active': isActive.bullet_list() }" title="Bulleted list" @click.prevent="commands.bullet_list">
            <b-icon icon="format-list-bulleted" size="is-small" />
          </b-button>
          <b-button :class="{ 'is-active': isActive.ordered_list() }" title="Numbered list" @click.prevent="commands.ordered_list">
            <b-icon icon="format-list-numbered" size="is-small" />
          </b-button>
          <b-button :class="{ 'is-active': isActive.blockquote() }" title="Quote" @click.prevent="commands.blockquote">
            <b-icon icon="format-quote-close" size="is-small" />
          </b-button>
          <b-button :class="{ 'is-active': isActive.code() }" title="Inline code" @click.prevent="commands.code">
            <b-icon icon="code-tags" size="is-small" />
          </b-button>
          <b-button :class="{ 'is-active': isActive.link() }" title="Link" @click.prevent="setLink(commands, getMarkAttrs('link'))">
            <b-icon icon="link-variant" size="is-small" />
          </b-button>
          <b-button title="Undo" @click.prevent="commands.undo">
            <b-icon icon="undo" size="is-small" />
          </b-button>
          <b-button title="Redo" @click.prevent="commands.redo">
            <b-icon icon="redo" size="is-small" />
          </b-button>
        </div>
      </div>
    </editor-menu-bar>
    <!-- 'content is-small' mirrors the classes the chatbot renders answers with,
         so editing is WYSIWYG. The editable ProseMirror element is only as tall
         as its content, so clicks on the surrounding padding focus it too. -->
    <editor-content
      :editor="editor"
      class="content is-small pa-md"
      style="min-height:220px; max-height:360px; overflow-y:auto; cursor:text"
      @click.native="focusEditor" />
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
  Blockquote,
  Bold,
  BulletList,
  Code,
  HardBreak,
  Heading,
  History,
  Italic,
  Link,
  ListItem,
  OrderedList,
  Underline
} from 'tiptap-extensions'

// Strips markup to decide whether an answer has any real text. Tiptap reports
// an empty document as '<p></p>', which should save as an empty string.
const isBlank = (html) => String(html || '').replace(/<[^>]*>/g, '').trim() === ''

export default {
  components: {
    EditorContent,
    EditorMenuBar
  },
  data () {
    return {
      editor: null,
      // The last value emitted upward; used to ignore the echo when the parent
      // writes it straight back into the `value` prop.
      lastEmitted: null
    }
  },
  methods: {
    focusEditor () {
      if (this.editor && !this.editor.focused) {
        this.editor.focus()
      }
    },
    setLink (commands, attrs) {
      const current = (attrs && attrs.href) || ''
      // With no text selected and no link under the cursor, tiptap's link
      // command has no range to mark, so insert the URL itself as linked text.
      const insertNew = this.editor.state.selection.empty && !current
      this.$buefy.dialog.prompt({
        message: current ? 'Update the link URL (leave blank to remove the link).' : 'Enter the link URL.',
        inputAttrs: {
          type: 'text',
          value: current,
          placeholder: 'https://',
          maxlength: 512,
          required: false
        },
        confirmText: current ? 'Update' : 'Add Link',
        trapFocus: true,
        onConfirm: (value) => {
          const href = (value || '').trim()
          if (insertNew) {
            if (href) this.insertLink(href)
          } else {
            commands.link({ href: href || null, target: '_blank' })
          }
          this.editor.focus()
        },
        onCancel: () => {
          this.editor.focus()
        }
      })
    },
    insertLink (href) {
      const { schema, state, view } = this.editor
      const mark = schema.marks.link.create({ href, target: '_blank' })
      const node = schema.text(href, [mark])
      view.dispatch(state.tr.replaceSelectionWith(node, false))
    }
  },
  // Create the editor before the first render so <editor-content> always
  // receives a non-null editor (its beforeDestroy hook assumes one).
  created () {
    this.editor = new Editor({
      content: this.value || '',
      extensions: [
        new Blockquote(),
        new BulletList(),
        new HardBreak(),
        new Heading({ levels: [3, 4] }),
        new ListItem(),
        new OrderedList(),
        new Bold(),
        new Code(),
        new Italic(),
        new Link({ openOnClick: false }),
        new Underline(),
        new History()
      ],
      onUpdate: ({ getHTML }) => {
        const html = getHTML()
        this.lastEmitted = isBlank(html) ? '' : html
        this.$emit('input', this.lastEmitted)
      }
    })
  },
  beforeDestroy () {
    if (this.editor) this.editor.destroy()
  },
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  watch: {
    // Only push external changes (e.g. an entry finishing loading) into the
    // editor; re-setting our own output would reset the cursor on every keystroke.
    value (value) {
      if (!this.editor || value === this.lastEmitted) return
      this.lastEmitted = value
      this.editor.setContent(value || '', false)
    }
  }
}
</script>
